/**
 * Desplegable de consulta del maestro de clientes desde el campo "Cuenta cliente".
 * Permite buscar por cuenta, nombre o NIF y volcar los datos del cliente en la factura.
 */
import { useState } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CustomerOption {
  taxId: string;
  accountCode: string;
  salesAccount: string;
  description: string;
  postalCode?: string;
}

interface Props {
  value: string;
  options: CustomerOption[];
  onSelect: (option: CustomerOption) => void;
  disabled?: boolean;
}

const CustomerMasterPicker = ({ value, options, onSelect, disabled }: Props) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-9 shrink-0"
          disabled={disabled || options.length === 0}
          title="Ver maestro de clientes"
          aria-label="Ver maestro de clientes"
        >
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar cuenta, nombre o NIF…" />
          <CommandList>
            <CommandEmpty>Sin resultados en el maestro.</CommandEmpty>
            <CommandGroup heading="Maestro de clientes">
              {options.map((o) => (
                <CommandItem
                  key={`${o.taxId}-${o.accountCode}`}
                  value={`${o.accountCode} ${o.description} ${o.taxId}`}
                  onSelect={() => {
                    onSelect(o);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      o.accountCode === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm">
                      <span className="font-mono">{o.accountCode || "—"}</span> · {o.description}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {o.taxId} {o.salesAccount ? `· venta ${o.salesAccount}` : ""}
                    </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CustomerMasterPicker;
